import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { getLeadJourneyIndicators } from "services/leadsService";
import ApplicationFormViewTab from "../LeadDetails/ApplicationFormViewTab";

export default function ApplicationFormModal({ isOpen, onClose, leadId, leadData, onLeadUpdated }) {
  const [progressData, setProgressData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIndicators = useCallback(async (forceRefresh = false) => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getLeadJourneyIndicators(leadId, forceRefresh);
      if (response.success && response.data) {
        setProgressData(response.data);
      } else {
        setError(response.message || "Failed to load application status");
      }
    } catch (err) {
      console.error("Error loading journey indicators in modal:", err);
      setError("Failed to load application status");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (isOpen && leadId) {
      fetchIndicators(true);
    } else {
      setProgressData({});
      setLoading(true);
      setError(null);
    }
  }, [isOpen, leadId, fetchIndicators]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[99998] flex items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => {}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-4xl origin-top flex-col overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none h-[90vh]">
            {/* HEADER */}
            <div className="border-b border-gray-200 dark:border-dark-600 px-6 py-4 flex items-center justify-between bg-gray-50 dark:bg-dark-700 shrink-0">
              <div>
                <DialogTitle as="h3" className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                  Application Form
                </DialogTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Complete the application form details below
                </p>
              </div>

              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-gray-300 transition-all duration-200"
              >
                <XMarkIcon className="size-5" />
              </Button>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-white dark:bg-dark-800">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-4" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading application details...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-500 font-medium">{error}</p>
                  <Button onClick={() => fetchIndicators(true)} className="mt-4" size="sm">
                    Retry
                  </Button>
                </div>
              ) : (
                <ApplicationFormViewTab
                  applicationId={progressData?.ApplicationId}
                  leadUserId={leadId}
                  leadData={leadData}
                  progressData={progressData}
                  isMobile={true}
                  refreshProgressJourney={fetchIndicators}
                  onLeadDataRefresh={onLeadUpdated}
                  selectedTab="application-details"
                />
              )}
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}
